import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { MdEdit } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { clearNxtFund, updateCampaignNtafId } from '~/services/campaign';
import { nxtFundUrl } from '~/common/nxtUrl';

const NTAF_ID_PATTERN = /^[A-Za-z0-9]{1,10}$/;

interface FundDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    fundId: string;
    onFetchFund: (fundId: string) => Promise<any>;
    onFetchCustomFields: (fundId: string) => Promise<any>;
    onUpdateFund?: (fundId: string, data: any) => Promise<any>;
    customTitle?: string;
    customMessage?: string;
    campaignId?: number;
    currentNtafId?: string | null;
    onNtafIdUpdated?: (newNtafId: string) => void;
    onUnlinked?: () => void;
}

const FundDetailsModal: React.FC<FundDetailsModalProps> = ({
    isOpen,
    onClose,
    fundId,
    onFetchFund,
    onFetchCustomFields,
    customTitle,
    customMessage,
    campaignId,
    currentNtafId,
    onNtafIdUpdated,
    onUnlinked,
}) => {
    const [loading, setLoading] = useState(false);
    const [fundData, setFundData] = useState<any>(null);
    const [customFields, setCustomFields] = useState<any[]>([]);
    const [fundFetchStatus, setFundFetchStatus] = useState<number | null>(null);
    const [displayNtafId, setDisplayNtafId] = useState<string>('');
    const [editingNtafId, setEditingNtafId] = useState(false);
    const [ntafIdDraft, setNtafIdDraft] = useState('');
    const [savingNtafId, setSavingNtafId] = useState(false);
    const [unlinking, setUnlinking] = useState(false);

    useEffect(() => {
        if (isOpen && fundId) {
            fetchFundDetails();
            fetchCustomFields();
            setDisplayNtafId(currentNtafId || '');
        } else if (!isOpen) {
            // Reset state when modal closes
            setFundData(null);
            setCustomFields([]);
            setFundFetchStatus(null);
            setEditingNtafId(false);
            setNtafIdDraft('');
            setDisplayNtafId('');
        }
    }, [isOpen, fundId, currentNtafId]);

    const handleSaveNtafId = async () => {
        const next = ntafIdDraft.trim().toUpperCase();
        if (!NTAF_ID_PATTERN.test(next)) {
            toast.error('NTAF ID must be 1-10 alphanumeric characters.');
            return;
        }
        if (!campaignId) {
            toast.error('Missing campaign id; cannot update NTAF ID.');
            return;
        }
        if (next === (displayNtafId || '').toUpperCase()) {
            setEditingNtafId(false);
            return;
        }
        setSavingNtafId(true);
        try {
            const res = await updateCampaignNtafId(campaignId, next);
            setDisplayNtafId(res.ntaf_id);
            setEditingNtafId(false);
            toast.success('NTAF ID updated in NXT and database');
            onNtafIdUpdated?.(res.ntaf_id);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update NTAF ID');
        } finally {
            setSavingNtafId(false);
        }
    };

    const fetchFundDetails = async () => {
        setLoading(true);
        try {
            const response = await onFetchFund(fundId);

            setFundFetchStatus(response?.status_code ?? null);
            if (response.success && response.data) {
                console.log('Fund data type field:', response.data.type);
                setFundData(response.data);
            } else if (response?.status_code === 404) {
                // Don't toast — show recovery panel instead.
            } else {
                toast.error(response.error || 'Failed to fetch fund details');
            }
        } catch (error) {
            toast.error('Error fetching fund details');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUnlink = async () => {
        if (!campaignId) {
            toast.error('Missing campaign id; cannot unlink fund.');
            return;
        }
        setUnlinking(true);
        try {
            await clearNxtFund(campaignId);
            toast.success('Fund unlinked. Choose a fund or create a new one.');
            onUnlinked?.();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to unlink fund');
        } finally {
            setUnlinking(false);
        }
    };

    const fetchCustomFields = async () => {
        try {
            const response = await onFetchCustomFields(fundId);
            
            if (response.success && response.data) {
                // The API returns either an array or an object with a 'value' array
                const fields = Array.isArray(response.data) 
                    ? response.data 
                    : response.data.value || [];
                console.log('Custom fields:', fields);
                setCustomFields(fields);
            } else {
                console.log('No custom fields or failed to fetch:', response.error);
                setCustomFields([]);
            }
        } catch (error) {
            console.error('Error fetching custom fields:', error);
            setCustomFields([]);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        // Extract just the date portion (YYYY-MM-DD) from ISO datetime
        const date = dateString.split('T')[0];
        // Format as MM/DD/YYYY
        const [year, month, day] = date.split('-');
        return `${month}/${day}/${year}`;
    };

    const formatCurrency = (value: any) => {
        if (!value || value === 0) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value);
    };

    if (!isOpen) return null;

    return (
        <div
            className='fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-2xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='text-gray-600 hover:text-gray-900 absolute right-4 top-4 text-2xl'
                    onClick={onClose}
                >
                    <RxCross2 />
                </button>

                <div className='p-8'>
                    <h2 className='mb-4 text-2xl font-bold'>
                        {customTitle || 'Fund Details'}
                    </h2>
                    {customMessage && (
                        <div className='mb-6 rounded-lg bg-green-50 p-4 text-green-800'>
                            <p className='text-sm'>{customMessage}</p>
                        </div>
                    )}

                    {loading ? (
                        <div className='flex justify-center py-12'>
                            <div className='h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
                        </div>
                    ) : fundData ? (
                        <div className='space-y-4'>
                            {/* NTAF Lookup ID */}
                            {campaignId && currentNtafId !== undefined && (
                                <div>
                                    <label className='mb-1 block text-sm font-medium text-gray-600'>
                                        NTAF Lookup ID
                                    </label>
                                    {editingNtafId ? (
                                        <div className='flex items-center gap-2'>
                                            <input
                                                type='text'
                                                value={ntafIdDraft}
                                                onChange={(e) =>
                                                    setNtafIdDraft(e.target.value)
                                                }
                                                maxLength={10}
                                                placeholder='Alphanumeric, max 10'
                                                className='flex-1 rounded border border-stroke px-3 py-2 text-sm font-mono uppercase focus:border-primary focus:outline-none'
                                                autoFocus
                                            />
                                            <button
                                                type='button'
                                                onClick={handleSaveNtafId}
                                                disabled={savingNtafId}
                                                className='rounded-full border-[2px] border-primary bg-primary px-[16px] py-[4px] text-[12px]/[16px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:opacity-50'
                                            >
                                                {savingNtafId ? 'Saving…' : 'Save'}
                                            </button>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setEditingNtafId(false);
                                                    setNtafIdDraft('');
                                                }}
                                                disabled={savingNtafId}
                                                className='rounded-full border-[2px] px-[16px] py-[4px] text-[12px]/[16px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50'
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className='flex items-center gap-2'>
                                            <span className='text-base font-mono'>
                                                {displayNtafId || 'N/A'}
                                            </span>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    const raw = displayNtafId || '';
                                                    const isInvalid =
                                                        !!raw &&
                                                        !NTAF_ID_PATTERN.test(raw);
                                                    const cleaned = raw
                                                        .toUpperCase()
                                                        .replace(/[^A-Z0-9]/g, '')
                                                        .slice(0, 10);
                                                    setNtafIdDraft(
                                                        isInvalid ? cleaned : raw
                                                    );
                                                    setEditingNtafId(true);
                                                }}
                                                className='p-1 rounded text-primary transition-colors hover:bg-gray-100'
                                                title='Edit NTAF Lookup ID in NXT and database'
                                            >
                                                <MdEdit size={16} />
                                            </button>
                                        </div>
                                    )}
                                    <p className='mt-1 text-xs text-gray-500'>
                                        Updates the lookup_id in NXT and{' '}
                                        <code>cf_patient_ntaf_id</code> in the database.
                                    </p>
                                </div>
                            )}

                            {/* Fund ID */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Fund ID
                                </label>
                                <div className='text-base'>{fundId}</div>
                                <a
                                    href={nxtFundUrl(fundId)}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='mt-1 inline-block text-sm text-primary hover:underline'
                                >
                                    View in Blackbaud →
                                </a>
                            </div>

                            {/* Description */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Description
                                </label>
                                <div className='text-base'>
                                    {fundData.description || 'N/A'}
                                </div>
                            </div>

                            {/* Category */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Category
                                </label>
                                <div className='text-base'>
                                    {fundData.fund_category?.name || 'N/A'}
                                </div>
                            </div>

                            {/* Type */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Type
                                </label>
                                <div className='text-base'>
                                    {fundData.type?.name || 
                                     fundData.type?.description || 
                                     fundData.type?.long_description || 
                                     (typeof fundData.type === 'string' ? fundData.type : 'N/A')}
                                </div>
                            </div>

                            {/* Start Date */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Start Date
                                </label>
                                <div className='text-base'>
                                    {formatDate(fundData.start_date)}
                                </div>
                            </div>

                            {/* Goal */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Goal
                                </label>
                                <div className='text-base'>
                                    {formatCurrency(fundData.goal?.value)}
                                </div>
                            </div>

                            {/* Status */}
                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    Status
                                </label>
                                <div className='text-base'>
                                    {fundData.inactive ? 'Inactive' : 'Active'}
                                </div>
                            </div>

                            {/* Custom Fields */}
                            {customFields.length > 0 && (
                                <div className='mt-6 border-t pt-6'>
                                    <h3 className='mb-4 text-lg font-semibold'>Custom Fields</h3>
                                    <div className='space-y-3'>
                                        {customFields.map((field: any, index: number) => (
                                            <div key={index}>
                                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                                    {field.category?.name || field.name || 'Custom Field'}
                                                </label>
                                                <div className='text-base'>
                                                    {field.value || field.text_value || field.date_value || 'N/A'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className='mt-8 flex gap-4'>
                                {campaignId && (
                                    <Link
                                        to={`/campaigns/${campaignId}`}
                                        className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90'
                                    >
                                        View Campaign
                                    </Link>
                                )}
                                <button
                                    type='button'
                                    onClick={onClose}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : fundFetchStatus === 404 ? (
                        <div className='space-y-4'>
                            <div className='rounded-lg bg-yellow-50 p-4 text-yellow-900'>
                                <p className='mb-2 text-sm font-semibold'>
                                    Fund not found in NXT
                                </p>
                                <p className='text-sm'>
                                    This campaign is linked to NXT fund ID{' '}
                                    <span className='font-mono font-semibold'>
                                        {fundId}
                                    </span>
                                    , but Blackbaud returned 404. The fund may
                                    have been deleted or the link is incorrect.
                                </p>
                            </div>

                            <div>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    NXT Fund Link (verify in Blackbaud)
                                </label>
                                <a
                                    href={nxtFundUrl(fundId)}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='break-all text-sm text-primary underline hover:no-underline'
                                >
                                    {nxtFundUrl(fundId)}
                                </a>
                            </div>

                            {campaignId ? (
                                <>
                                    <p className='text-sm text-gray-600'>
                                        Unlink this fund to relink an existing
                                        fund by ID or create a new one.
                                    </p>
                                    <div className='mt-6 flex justify-end gap-3 border-t pt-4'>
                                        <button
                                            type='button'
                                            onClick={onClose}
                                            disabled={unlinking}
                                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50'
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type='button'
                                            onClick={handleUnlink}
                                            disabled={unlinking}
                                            className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:opacity-50'
                                        >
                                            {unlinking
                                                ? 'Unlinking…'
                                                : 'Unlink and choose another'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p className='text-sm text-gray-500'>
                                    Open this fund from a campaign page to
                                    unlink and relink.
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className='py-12 text-center text-gray-500'>
                            No fund data available
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FundDetailsModal;

